import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { CheckCircleIcon } from "@heroicons/react/24/solid";

import useDepollution from "../../data/useDepollution";
import useVehicleSingle from "../../data/useVehicleSingle";
import { useAuth } from "../../contexts/AuthContext";
import DepollutionTimerCard from "../../components/depollution/DepollutionTimerCard";
import LoadingWheel from "../../components/shared/LoadingWheel";
import ContactFromId from "../../components/contacts/ContactFromId";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import { ButtonInput } from "../../components/shared/Inputs/ButtonInput";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import Card from "../../components/shared/Card/Card";
import DepollutionButton from "../../components/depollution/DepollutionButton";
import { CheckFeatureFlag } from "../../hooks/useFeatureFlag";
import DepollutionVehicleSummary from "../../components/depollution/DepollutionVehicleSummary";
import DepollutionPartsEditor from "../../components/depollution/DepollutionPartsEditor";
import CreateJobs from "../../components/jobs/CreateJobs";

export default function Depollution({ depollutionId: depollutionIdProp, vehicleId, collapsible = false }: { depollutionId?: string; vehicleId?: string; collapsible?: boolean; }) {
  const { depollutionId: depollutionIdParam } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const isStandalonePage = depollutionIdProp === undefined && vehicleId === undefined;
  const isTMEEnabled = CheckFeatureFlag("TME");
  const depollutionLabel = "Depollution";
  // Set by the Depollution list when the user scans a vehicle tag: start the
  // timer on arrival.
  const autoStartTimer =
    isStandalonePage && !!(location.state as { autoStartTimer?: boolean } | null)?.autoStartTimer;

  const { setInfo } = usePageNameContext();
  const { userId } = useAuth();
  const { saveHandlers } = useDefaultCRUDHandlers(`${depollutionLabel} item`);

  const { vehicle: vehicleQuery } = useVehicleSingle(vehicleId);
  const vehicleDepollutionId = vehicleQuery.data?.data?.depollutionId;
  const resolvedDepollutionId = depollutionIdProp ?? (vehicleId ? vehicleDepollutionId ?? undefined : depollutionIdParam);

  const { depollution, depollutionItems, depollutionSteps, saveDepollutionItem } = useDepollution(resolvedDepollutionId);
  const { vehicle: depollutionVehicleQuery } = useVehicleSingle(depollution.data?.data?.vehicleId);
  const [draftValues, setDraftValues] = useState<Record<string, string>>({});
  const [savingStepId, setSavingStepId] = useState<string | null>(null);

  const steps = useMemo(
    () => [...(depollutionSteps.data?.data ?? [])].sort((a, b) => a.order - b.order),
    [depollutionSteps.data?.data]
  );
  const items = depollutionItems.data?.data ?? [];
  const vehicle = depollution.data?.data?.vehicle;
  const fullVehicle = depollutionVehicleQuery.data?.data ?? vehicleQuery.data?.data ?? vehicle;

  useEffect(() => {
    if (!isStandalonePage || depollution.isLoading) return;

    if (!depollution.data?.data) {
      setInfo({
        name: `${depollutionLabel} not found`,
        desc: `The ${depollutionLabel.toLowerCase()} record you are looking for does not exist`,
      });
      return;
    }

    setInfo({
      name: `${depollutionLabel} - ${vehicle?.vrm ?? "Vehicle"}`,
      desc: `${vehicle?.combinedMake ?? ""} ${vehicle?.combinedModel ?? ""}`.trim(),
    });
  }, [isStandalonePage, depollution.isLoading, depollution.data?.data?.id, vehicle?.vrm, vehicle?.combinedMake, vehicle?.combinedModel, setInfo, depollutionLabel]);

  useEffect(() => {
    if (!depollutionItems.data?.data) return;

    const nextValues: Record<string, string> = {};
    for (const item of depollutionItems.data.data) {
      nextValues[item.depollutionStepId] = item.value ?? "";
    }
    setDraftValues(nextValues);
  }, [depollutionItems.data?.data]);

  const rowData = useMemo(() => {
    return steps.map((step) => {
      const item = items.find((x) => x.depollutionStepId === step.id);
      return {
        step,
        item,
        isCompleted: !!item?.completed,
      };
    });
  }, [steps, items]);

  const completedCount = rowData.filter((x) => x.isCompleted).length;
  const totalCount = rowData.length;
  const allCompleted = totalCount > 0 && completedCount === totalCount;

  const parseOptions = (options: string | null) => {
    if (!options) return [];
    return options
      .split(",")
      .map((x) => x.trim())
      .filter(Boolean);
  };

  const saveStep = (depollutionStepId: string, isCompleted: boolean) => {
    const vehicleId = depollution.data?.data?.vehicleId;
    if (!vehicleId) return;

    setSavingStepId(depollutionStepId);
    saveDepollutionItem.mutate(
      {
        vehicleId,
        depollutionStepId,
        value: draftValues[depollutionStepId] ?? "",
        isCompleted,
      },
      {
        onSuccess: saveHandlers.onSuccess,
        onError: saveHandlers.onError,
        onSettled: () => setSavingStepId(null),
      }
    );
  };

  const saveStepValue = (depollutionStepId: string, value: string, isCompleted: boolean) => {
    const vehicleId = depollution.data?.data?.vehicleId;
    if (!vehicleId) return;

    setDraftValues((prev) => ({ ...prev, [depollutionStepId]: value }));
    setSavingStepId(depollutionStepId);
    saveDepollutionItem.mutate(
      {
        vehicleId,
        depollutionStepId,
        value,
        isCompleted,
      },
      {
        onSuccess: saveHandlers.onSuccess,
        onError: saveHandlers.onError,
        onSettled: () => setSavingStepId(null),
      }
    );
  };

  const isDepollutionDataLoading = !!resolvedDepollutionId && (depollution.isLoading || depollutionItems.isLoading);
  if ((vehicleId && !depollutionIdProp && vehicleQuery.isLoading) || isDepollutionDataLoading || depollutionSteps.isLoading) {
    return (
      <Card title={depollutionLabel} collapsible={false}>
        <div className="p-4">
          <LoadingWheel />
        </div>
      </Card>
    );
  }

  if (vehicleId && !depollution.data?.data) {
    return (
      <Card title={depollutionLabel} collapsible={false}>
        <div className="p-4">
          <DepollutionButton
            vehicleId={vehicleId}
            depollutionId={vehicleDepollutionId}
            openInNewTab={false}
            navigateOnCreate={false}
          />
        </div>
      </Card>
    );
  }

  if (!depollution.data?.data) return null;

  return (
    <div className="flex flex-col gap-4">

      {isStandalonePage && fullVehicle && (
        <Card>
          <DepollutionVehicleSummary
            vehicle={fullVehicle}
            completedCount={completedCount}
            totalCount={totalCount}
            depollutionLabel={depollutionLabel}
          />
        </Card>
      )}

      <DepollutionTimerCard
        depollutionId={resolvedDepollutionId}
        userId={userId}
        autoStart={autoStartTimer}
        enableScanToggle={isStandalonePage}
        onSessionFinished={isStandalonePage ? () => navigate("/depollution") : undefined}
      />

      <Card
        title={`${depollutionLabel} ${completedCount}/${totalCount} ${completedCount === totalCount ? "✔" : ""}`}
        collapsible={collapsible}
        defaultOpen={collapsible ? !allCompleted : true}
        titleClassName={collapsible && completedCount === totalCount ? "bg-green-300" : ""}
      >
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Item</th>
              <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Value</th>
              <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Completed By</th>
              <th className="px-4 py-2 text-center text-xs font-semibold text-gray-600">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rowData.map(({ step, item, isCompleted }) => (
              <tr key={step.id} className="bg-white hover:bg-gray-50">
                <td className="px-4 py-2 text-sm">
                  <div>{step.name}</div>
                  {step.subtitle && (
                    <div className="text-xs text-gray-500">{step.subtitle}</div>
                  )}
                </td>
                <td className="px-4 py-2 text-sm">
                  {step.needsValue ? (
                    (() => {
                      const parsedOptions = parseOptions(step.options);
                      if (parsedOptions.length > 0) {
                        if (parsedOptions.length <= 3) {
                          const selectedValue = draftValues[step.id] ?? "";
                          return (
                            <div className="flex flex-wrap gap-2">
                              {parsedOptions.map((opt) => {
                                const selected = selectedValue === opt;
                                return (
                                  <button
                                    key={opt}
                                    type="button"
                                    disabled={savingStepId === step.id}
                                    onClick={() => saveStepValue(step.id, opt, true)}
                                    className={`rounded-md border px-3 py-1.5 text-sm font-medium shadow-sm ${
                                      selected
                                        ? "border-primary-600 bg-primary-600 text-white hover:bg-primary-700"
                                        : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                                    }`}
                                  >
                                    {opt}
                                  </button>
                                );
                              })}
                            </div>
                          );
                        }

                        return (
                          <select
                            value={draftValues[step.id] ?? ""}
                            disabled={savingStepId === step.id}
                            onChange={(e) => saveStepValue(step.id, e.target.value, true)}
                            className="w-full max-w-xs rounded-md border border-gray-300 px-2 py-1 text-sm"
                          >
                            <option value="">Select...</option>
                            {parsedOptions.map((opt) => (
                              <option key={opt} value={opt}>
                                {opt}
                              </option>
                            ))}
                          </select>
                        );
                      }

                      return (
                        <div className="flex items-center gap-2">
                          <input
                            value={draftValues[step.id] ?? ""}
                            onChange={(e) =>
                              setDraftValues((prev) => ({ ...prev, [step.id]: e.target.value }))
                            }
                            className="w-full max-w-xs rounded-md border border-gray-300 px-2 py-1 text-sm"
                            placeholder="Enter value"
                          />
                          <ButtonInput
                            label="Save"
                            isSubmit={false}
                            classes=""
                            isLoading={savingStepId === step.id}
                            onClick={() => saveStep(step.id, isCompleted)}
                          />
                        </div>
                      );
                    })()
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </td>
                <td className="px-4 py-2 text-sm text-gray-700">
                  {item?.completedBy ? <ContactFromId contactId={item.completedBy} /> : <span className="text-gray-400">-</span>}
                </td>
                <td className="px-4 py-2 text-center">
                  <ButtonInput
                    isSubmit={false}
                    onClick={() => saveStep(step.id, !isCompleted)}
                    isLoading={savingStepId === step.id}
                    classes={undefined}
                  >
                    <div className="flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-gray-700 shadow-sm hover:bg-gray-50">
                      <CheckCircleIcon className={`h-5 w-5 ${isCompleted ? "text-green-500" : "text-gray-300"}`} />
                      {isCompleted ? "Completed" : "Mark Complete"}
                    </div>
                  </ButtonInput>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {/* {isStandalonePage && fullVehicle && (
        <DepollutionPartsEditor vehicle={fullVehicle} />
      )} */}
    </div>
  );
}
